import { PencilIcon } from "@heroicons/react/24/outline";
import {
  Button,
  getInput,
  IconButton,
  Modal,
} from "../../../../../../../components/ui";
import EditNameModal from "./EditNameModal";
import { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import { CREATE_LIST as createListMutation } from "../../../../../../../graphql/mutation/List";
import { LIST_TYPES } from "../../../../../../../constants/listTypes";
import useEntityImageUpload from "../../../../../../../hooks/useEntityImageUpload";

const ImageField = props => {
  const { value, setValue, image, contactId } = props || {};
  const [uploadedImage, setUploadedImage] = useState();

  useEffect(() => {
    return () => {
      if (uploadedImage) URL.revokeObjectURL(uploadedImage);
    };
  }, [uploadedImage]);

  const { uploadEntityImage, deleteEntityImage, loading } =
    useEntityImageUpload({ refetchQueries: ["FetchContact"] });

  const handleUpload = async (e, closeModal) => {
    const files = e.files;

    if (files.length) {
      const result = await uploadEntityImage({
        entityType: "contact",
        entityId: contactId,
        file: files[0],
      });
      if (result.success) {
        // The signed URL is unchanged when the existing image is overwritten.
        setUploadedImage(URL.createObjectURL(files[0]));
        closeModal();
      }
    }
  };

  const handleDetach = attachment => {
    setValue?.(value.filter(item => item !== attachment));
  };

  const handleDelete = async () => {
    const result = await deleteEntityImage({
      entityType: "contact",
      entityId: contactId,
    });
    if (result.success) setUploadedImage("");
  };

  const inputProps = {
    ...props,
    onChange: handleUpload,
    handleDelete,
    handleDetach,
    className: "border-neutral-400",
    variant: "modal",
    value: uploadedImage ?? image,
    loading,
  };

  return <div className="flex bg-neutral-200">{getInput(inputProps)}</div>;
};

const Header = props => {
  const { contact, contactId, disabled, image } = props || {};
  const queryParams = new URLSearchParams(window.location.search);
  const targetListId = queryParams.get("listId");
  const [createList, { loading }] = useMutation(createListMutation());

  const [successOpModal, setSuccessOpModal] = useState({
    isOpen: false,
    message: "",
  });

  const imageInput = {
    disabled,
    name: "meta.files",
    label: "",
    type: "img",
    isMultiSelect: false,
    uploadType: "file(s)",
    h: "h-[200px]",
    w: "w-[200px]",
    text: "Add Image",
    textClassName:
      "bg-white border border-gray-400 px-6 py-2 rounded-md text-[12px]",
    uploadButtonClassName: "px-8 py-2",
    uploadModalTitle: `Upload Image for ${props.leftText || ""}`,
    image,
    contactId,
  };

  const modalProps = {
    body: EditNameModal,
    contact,
    closeOnBackdrop: true,
    hideCloseButton: true,
    scale: "md",
  };

  const handleAddToSelectList = async e => {
    e.preventDefault();
    const res = await createList({
      variables: {
        input: {
          name: `select_list_working_${Date.now()}`,
          listItemIds: [contact?.id],
          typeId: LIST_TYPES.CONTACT_WORKING,
          ...(targetListId ? { listId: targetListId } : {}),
        },
      },
    });
    if (res?.data?.createList?.success) {
      setSuccessOpModal({
        isOpen: true,
        message: "The contact has been added to select list.",
      });
    }
  };

  return (
    <div className="col-span-1 flex cursor-pointer flex-row justify-between border-b border-gray-400 pb-12">
      <div className="relative flex w-full flex-row items-end">
        <Button
          loading={loading}
          className={`absolute right-0 top-0 flex items-center`}
          label={loading ? "Adding..." : "Add to select list"}
          onClick={handleAddToSelectList}
          disabled={loading}
        />
        <div className="pr-8">
          <ImageField key={contactId} {...imageInput} />
        </div>
        <div className="group flex">
          <h1 className="text-xl font-bold">
            {props.leftText?.trim() || "[Unnamed Contact]"}
          </h1>
          {!disabled && (
            <Modal {...modalProps}>
              <IconButton
                className="hidden group-hover:block"
                variant="clean"
                title="Edit Name"
              >
                <PencilIcon
                  className={`h-4 w-4 cursor-pointer text-gray-400`}
                />
              </IconButton>
            </Modal>
          )}
        </div>
      </div>
      <Modal
        scale="sm"
        closeOnBackdrop={false}
        hideCloseButton={true}
        isOpen={successOpModal.isOpen}
        onClose={() => setSuccessOpModal({ isOpen: false, message: "" })}
        renderBody={({ closeModal }) => (
          <div className="w-full">
            <div className="mb-5 text-lg font-medium leading-6 text-gray-900">
              {successOpModal.message}
            </div>
            <Button
              label={"Close"}
              onClick={() => {
                closeModal();
              }}
            />
          </div>
        )}
      />
    </div>
  );
};

export default Header;
